// Permission keys × view scope (plan 7.2). The server decides every request with these; the web only hides menus/buttons.
// Source of truth: plan 7.2.2 tables 1–2 (35 keys in 6 bundles) — names match mockup data.js PERM_KEYS 1:1.
import { z } from 'zod';

/** The 6 bundles the 35 keys fold into on SCR-11 (plan 7.2.2, 8.4.11). */
export const PERMISSION_BUNDLES = ['usage', 'records', 'content', 'work', 'export', 'admin'] as const;
export const PermissionBundle = z.enum(PERMISSION_BUNDLES);
export type PermissionBundle = z.infer<typeof PermissionBundle>;

/** The 35 permission keys in plan order (table 1 then table 2). */
export const PERMISSION_KEYS = [
  // ① usage
  'usage.view',
  'cost.view',
  'stats.view',
  // ② records & people
  'logs.list',
  'people.view',
  'people.identity',
  'people.individual',
  // ③ question & answer text
  'logs.prompt',
  'logs.answer',
  // ④ work detail
  'logs.files',
  'logs.diff',
  'logs.commands',
  'logs.tool_output',
  'logs.raw',
  // ⑤ export
  'export.aggregate',
  'export.rows',
  'export.identity',
  'export.content',
  // ⑥ admin functions
  'people.manage',
  'teams.manage',
  'roles.manage',
  'policy.manage',
  'notice.manage',
  'integrations.manage',
  'keys.view',
  'devices.revoke_any',
  'budget.manage',
  'deletion.process',
  'legal_hold.manage',
  'access.approve',
  'audit.view',
  'audit.verify',
  'retention.manage',
  'glossary.edit',
  'system.view',
] as const;
export const PermissionKey = z.enum(PERMISSION_KEYS);
export type PermissionKey = z.infer<typeof PermissionKey>;

export const PERMISSION_BUNDLE_OF: Record<PermissionKey, PermissionBundle> = {
  'usage.view': 'usage',
  'cost.view': 'usage',
  'stats.view': 'usage',
  'logs.list': 'records',
  'people.view': 'records',
  'people.identity': 'records',
  'people.individual': 'records',
  'logs.prompt': 'content',
  'logs.answer': 'content',
  'logs.files': 'work',
  'logs.diff': 'work',
  'logs.commands': 'work',
  'logs.tool_output': 'work',
  'logs.raw': 'work',
  'export.aggregate': 'export',
  'export.rows': 'export',
  'export.identity': 'export',
  'export.content': 'export',
  'people.manage': 'admin',
  'teams.manage': 'admin',
  'roles.manage': 'admin',
  'policy.manage': 'admin',
  'notice.manage': 'admin',
  'integrations.manage': 'admin',
  'keys.view': 'admin',
  'devices.revoke_any': 'admin',
  'budget.manage': 'admin',
  'deletion.process': 'admin',
  'legal_hold.manage': 'admin',
  'access.approve': 'admin',
  'audit.view': 'admin',
  'audit.verify': 'admin',
  'retention.manage': 'admin',
  'glossary.edit': 'admin',
  'system.view': 'admin',
};

export const keysOfBundle = (bundle: PermissionBundle): PermissionKey[] =>
  PERMISSION_KEYS.filter((k) => PERMISSION_BUNDLE_OF[k] === bundle);

/** A matrix cell: off ('—') or one of the 3 view scopes (plan 7.2.1). Ordered self < team < all. */
export const PERMISSION_LEVELS = ['off', 'self', 'team', 'all'] as const;
export const PermissionLevel = z.enum(PERMISSION_LEVELS);
export type PermissionLevel = z.infer<typeof PermissionLevel>;
export const LEVEL_RANK: Record<PermissionLevel, 0 | 1 | 2 | 3> = { off: 0, self: 1, team: 2, all: 3 };
export const maxLevel = (a: PermissionLevel, b: PermissionLevel): PermissionLevel => (LEVEL_RANK[a] >= LEVEL_RANK[b] ? a : b);
export const levelAtLeast = (have: PermissionLevel, need: PermissionLevel): boolean => LEVEL_RANK[have] >= LEVEL_RANK[need];

/** Full map key → level (every key present). Used for role definitions and a user's effective permissions. */
export const PermissionMap = z.record(PermissionKey, PermissionLevel);
export type PermissionMap = z.infer<typeof PermissionMap>;
/** Sparse map for edits/diffs (only changed keys). */
export const PermissionPatch = z.partialRecord(PermissionKey, PermissionLevel);
export type PermissionPatch = z.infer<typeof PermissionPatch>;

export const emptyPermissionMap = (): PermissionMap =>
  Object.fromEntries(PERMISSION_KEYS.map((k) => [k, 'off'])) as PermissionMap;

/** Effective permission = union (max level per key) of every role the person holds (plan 7.1.3 rule 2). */
export function unionPermissions(maps: readonly PermissionMap[]): PermissionMap {
  const out = emptyPermissionMap();
  for (const m of maps) for (const k of PERMISSION_KEYS) out[k] = maxLevel(out[k], m[k]);
  return out;
}

/**
 * Keys marked `*` in plan 7.2.2: someone else's value is only visible after the fixed control flow (plan 7.5:
 * reason · step-up re-auth · per-conversation grant · audit · owner notice). Own records skip the flow.
 */
export const FLOW_KEYS = ['logs.prompt', 'logs.answer', 'logs.files', 'logs.diff', 'logs.commands', 'logs.tool_output', 'logs.raw'] as const satisfies readonly PermissionKey[];

/**
 * Changes that widen these keys need 2-person approval (plan 7.4.2 "글·실명·내보내기 권한이 늘어나는 변경",
 * set taken from the mockup DUAL_KEYS: text/work keys + identity + individual view + row/identity/content export).
 */
export const DUAL_APPROVAL_KEYS = [
  'logs.prompt',
  'logs.answer',
  'logs.files',
  'logs.diff',
  'logs.commands',
  'logs.tool_output',
  'logs.raw',
  'people.identity',
  'people.individual',
  'export.rows',
  'export.identity',
  'export.content',
] as const satisfies readonly PermissionKey[];

/** Bundles ③·④: a role granting any of these at team/all scope must carry an expiry date (plan 7.4.2). */
export const EXPIRY_REQUIRED_KEYS = ['logs.prompt', 'logs.answer', 'logs.files', 'logs.diff', 'logs.commands', 'logs.tool_output', 'logs.raw'] as const satisfies readonly PermissionKey[];

/**
 * MFA + prohibited-use pledge targets (plan 7.2.2 note, 7.7, 11.4.3): holders of team/all scope on text, work detail,
 * identity, individual view, role management, identity/text export or key management.
 */
export const MFA_KEYS = [
  ...EXPIRY_REQUIRED_KEYS,
  'people.identity',
  'people.individual',
  'roles.manage',
  'export.identity',
  'export.content',
  'integrations.manage',
] as const satisfies readonly PermissionKey[];

const isWide = (lv: PermissionLevel): boolean => LEVEL_RANK[lv] >= LEVEL_RANK.team;

/** True when the effective permissions make the person an MFA target (default: every role except member/stats_viewer). */
export const requiresMfa = (perms: PermissionMap): boolean => MFA_KEYS.some((k) => isWide(perms[k]));
/** The prohibited-use pledge has the same target set as MFA (plan 7.7). */
export const requiresPledge = requiresMfa;
/** Admin session (idle 30 min / absolute 8 h) = MFA target or any admin-bundle key (plan 11.4.2). */
export const isAdminSession = (perms: PermissionMap): boolean =>
  requiresMfa(perms) || keysOfBundle('admin').some((k) => perms[k] !== 'off');
/** A role whose text/work keys reach team/all needs an expiry on every grant (plan 7.4.2). */
export const grantNeedsExpiry = (perms: PermissionMap): boolean => EXPIRY_REQUIRED_KEYS.some((k) => isWide(perms[k]));
/** Keys whose level rises from `before` to `after` and that require 2-person approval. */
export const dualApprovalRaises = (before: PermissionMap, after: PermissionMap): PermissionKey[] =>
  DUAL_APPROVAL_KEYS.filter((k) => LEVEL_RANK[after[k]] > LEVEL_RANK[before[k]]);

/** Subset delegation check (plan 7.4.1): every cell of `wanted` must be ≤ the grantor's effective level. */
export const exceedsGrantor = (grantor: PermissionMap, wanted: PermissionMap): PermissionKey[] =>
  PERMISSION_KEYS.filter((k) => LEVEL_RANK[wanted[k]] > LEVEL_RANK[grantor[k]]);

/** Static description of one key (names come from the web i18n `perm.<key>` / `perm.<key>.desc`). */
export const PermissionDef = z.object({
  key: PermissionKey,
  bundle: PermissionBundle,
  /** Opening someone else's value goes through the fixed control flow (`*`). */
  flow: z.boolean(),
  /** Widening it needs 2-person approval. */
  dualApproval: z.boolean(),
  /** team/all scope makes the holder an MFA + pledge target. */
  mfa: z.boolean(),
  /** P6 optional module (budget). */
  optionalModule: z.boolean(),
});
export type PermissionDef = z.infer<typeof PermissionDef>;

export const PERMISSION_DEFS: readonly PermissionDef[] = PERMISSION_KEYS.map((key) => ({
  key,
  bundle: PERMISSION_BUNDLE_OF[key],
  flow: (FLOW_KEYS as readonly string[]).includes(key),
  dualApproval: (DUAL_APPROVAL_KEYS as readonly string[]).includes(key),
  mfa: (MFA_KEYS as readonly string[]).includes(key),
  optionalModule: key === 'budget.manage',
}));
